// src/pages/blog/PostChecklist.jsx
import React from "react";
import BlogHero from "../blog/BlogHero";
import BlogTip from "../blog/BlogTip";
import BlogImage from "../blog/BlogImage";
import BlogHeader from "../blog/BlogHeader";
import BlogFooter from "../blog/BlogFooter";

import {
	CheckCircle,
	Globe,
	CalendarCheck,
	CreditCard,
	Bell,
	Gift,
	BarChart3,
} from "lucide-react";

import heroImg from "../../assets/blog/checklist-hero.jpg";
import ejemploImg from "../../assets/blog/checklist-ejemplo.jpg";

export default function PostChecklist() {
	return (
		<div className="font-poppins text-gray-800">
			{/* Header fijo */}
			<BlogHeader />

			{/* Hero principal */}
			<BlogHero
				title="Checklist de digitalización para tu negocio"
				subtitle="Comprueba si tu negocio está realmente preparado para competir en la era digital."
				image={heroImg}
			/>

			<main className="max-w-4xl mx-auto px-6 py-12 space-y-16">
				{/* Introducción */}
				<section>
					<p className="text-lg leading-relaxed mb-6">
						Muchos negocios creen estar digitalizados porque dependen únicamente
						de marketplaces o redes sociales. El problema es que estas
						plataformas limitan tu visibilidad, te hacen competir con cientos de
						negocios similares y, en muchos casos, te cobran comisiones o no te
						permiten acceder a los datos de tus clientes.
					</p>
					<p className="text-lg leading-relaxed">
						Este checklist te ayudará a identificar en qué punto estás y qué
						pasos faltan para que tu negocio sea realmente competitivo en la era
						digital.
					</p>
				</section>

				{/* Checklist */}
				<section>
					<h2 className="text-2xl font-bold mb-8">
						El checklist de digitalización
					</h2>
					<ul className="space-y-6 text-lg text-gray-700">
						<li className="flex items-start">
							<Globe className="w-6 h-6 text-[#43bd98] mr-3 mt-1 flex-shrink-0" />
							<div>
								<strong>Presencia online:</strong> ¿tienes una web o landing
								profesional donde los clientes te encuentren?
							</div>
						</li>
						<li className="flex items-start">
							<CalendarCheck className="w-6 h-6 text-[#43bd98] mr-3 mt-1 flex-shrink-0" />
							<div>
								<strong>Reservas o pedidos online:</strong> ¿los clientes pueden
								reservar o pedir sin llamar por teléfono?
							</div>
						</li>
						<li className="flex items-start">
							<CreditCard className="w-6 h-6 text-[#43bd98] mr-3 mt-1 flex-shrink-0" />
							<div>
								<strong>Pagos digitales:</strong> ¿aceptas pagos con tarjeta o
								móvil de forma integrada?
							</div>
						</li>
						<li className="flex items-start">
							<Bell className="w-6 h-6 text-[#43bd98] mr-3 mt-1 flex-shrink-0" />
							<div>
								<strong>Comunicación directa:</strong> ¿usas notificaciones
								push, email o SMS para conectar con tus clientes?
							</div>
						</li>
						<li className="flex items-start">
							<Gift className="w-6 h-6 text-[#43bd98] mr-3 mt-1 flex-shrink-0" />
							<div>
								<strong>Fidelización:</strong> ¿ofreces cupones, bonos o
								programas de puntos para que los clientes repitan?
							</div>
						</li>
						<li className="flex items-start">
							<BarChart3 className="w-6 h-6 text-[#43bd98] mr-3 mt-1 flex-shrink-0" />
							<div>
								<strong>Datos y métricas:</strong> ¿puedes medir ventas,
								recurrencia y horarios más demandados?
							</div>
						</li>
					</ul>
				</section>

				{/* Imagen ilustrativa */}
				<BlogImage
					src={ejemploImg}
					alt="Ejemplo checklist digitalización"
					caption="Con Applaby, cada paso del checklist se integra en una sola plataforma, lista para tu negocio en pocos días."
				/>

				{/* Interpretación */}
				<section>
					<h2 className="text-2xl font-bold mb-6">
						¿Cómo interpretar tu checklist?
					</h2>
					<p className="text-lg leading-relaxed mb-4">
						Revisa cuántos puntos de la lista puedes marcar:
					</p>
					<ul className="space-y-3 text-lg text-gray-700">
						<li className="flex items-center gap-2">
							<CheckCircle className="w-5 h-5 text-[#43bd98] flex-shrink-0" />
							Si marcas pocos puntos → aún dependes de procesos manuales.
						</li>
						<li className="flex items-center gap-2">
							<CheckCircle className="w-5 h-5 text-[#43bd98] flex-shrink-0" />
							Si marcas la mayoría → vas por buen camino, pero todavía hay
							oportunidades de mejora.
						</li>
						<li className="flex items-center gap-2">
							<CheckCircle className="w-5 h-5 text-[#43bd98] flex-shrink-0" />
							Si los marcas todos → tu negocio ya es digital-first.
						</li>
					</ul>
				</section>

				{/* Conclusión */}
				<section>
					<h2 className="text-2xl font-bold mb-4">Conclusión</h2>
					<p className="text-lg leading-relaxed mb-6">
						El checklist es solo el punto de partida. La verdadera
						transformación digital no consiste en tener herramientas sueltas,
						sino en integrarlas de forma que trabajen juntas para mejorar la
						experiencia del cliente y optimizar la gestión del negocio.
					</p>
					<p className="text-lg leading-relaxed mb-6">
						Con <strong>Applaby</strong> puedes cubrir la mayoría de estos
						puntos desde un solo lugar: tu propia app y web de pedidos o
						reservas, completamente personalizadas con tu marca, listas en pocos
						días y sin inversión inicial.
					</p>
					<p className="text-lg leading-relaxed">
						La digitalización no tiene por qué ser complicada ni costosa.
						Empieza por lo esencial y apóyate en soluciones que te den control,
						datos y fidelización real. Cuanto antes des el paso, antes notarás
						el impacto en tus clientes y en tus ingresos.
					</p>
				</section>

				{/* CTA */}
				<div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
					<h3 className="text-xl font-semibold mb-3 text-gray-900">
						¿Quieres comprobarlo en tu negocio?
					</h3>
					<p className="text-gray-600 mb-5">
						Solicita una demo gratuita y descubre cómo digitalizar tu negocio en
						menos de una semana.
					</p>
					<a
						href="https://applaby.com/app-demo/"
						target="_blank"
						rel="noreferrer"
						className="inline-block bg-[#43bd98] text-white px-6 py-3 rounded-lg font-medium shadow hover:bg-[#37a885] transition"
					>
						Solicitar demo gratis
					</a>
				</div>
			</main>

			<BlogFooter />
		</div>
	);
}
